import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { designSystem, commonStyles } from '../../../styles/design-system';
import { supabase } from '../../../utils/supabaseClient';
import { 
  EditIcon, 
  TrashIcon, 
  ExternalLinkIcon, 
  UserIcon, 
  MapPinIcon,
  CustomerServiceIcon,
  SaveIcon 
} from '../../../utils/IconWrapper';

const ProfileContainer = styled(motion.div)`
  ${commonStyles.card}
  margin-bottom: ${designSystem.spacing.xl};
  position: relative;
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: ${designSystem.spacing.lg};
  align-items: start;
  margin-bottom: ${designSystem.spacing.xl};
  padding-bottom: ${designSystem.spacing.lg};
  border-bottom: 2px solid ${designSystem.colors.border.light};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${designSystem.colors.background.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${designSystem.typography.fontSize['3xl']};
  color: #000000;
  font-weight: ${designSystem.typography.fontWeight.bold};
  box-shadow: ${designSystem.shadows.lg};
`;

const UserInfo = styled.div`
  h2 {
    color: ${designSystem.colors.text.primary};
    font-family: ${designSystem.typography.fontFamily.secondary};
    font-size: ${designSystem.typography.fontSize['2xl']};
    margin-bottom: ${designSystem.spacing.sm};
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
  }

  .email {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.lg};
    margin-bottom: ${designSystem.spacing.sm};
  }

  .status-badges {
    display: flex;
    gap: ${designSystem.spacing.sm};
    flex-wrap: wrap;
  }
`;

const StatusBadge = styled.span<{ status: 'verified' | 'pending' | 'blocked' | 'vip' }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.full};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'verified':
        return `
          background: ${designSystem.colors.status.success}20;
          color: ${designSystem.colors.status.success};
        `;
      case 'pending':
        return `
          background: ${designSystem.colors.status.warning}20;
          color: ${designSystem.colors.status.warning};
        `;
      case 'blocked':
        return `
          background: ${designSystem.colors.status.error}20;
          color: ${designSystem.colors.status.error};
        `;
      case 'vip':
        return `
          background: ${designSystem.colors.primary.main}20;
          color: ${designSystem.colors.primary.main};
        `;
      default:
        return `
          background: ${designSystem.colors.secondary.main}20;
          color: ${designSystem.colors.secondary.main};
        `;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  flex-wrap: wrap;

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' | 'warning' }>`
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: ${designSystem.colors.status.error};
          color: #ffffff;
          border: 2px solid ${designSystem.colors.status.error};
          
          &:hover {
            background: transparent;
            color: ${designSystem.colors.status.error};
          }
        `;
      case 'warning':
        return `
          background: ${designSystem.colors.status.warning};
          color: #000000;
          border: 2px solid ${designSystem.colors.status.warning};
          
          &:hover {
            background: transparent;
            color: ${designSystem.colors.status.warning};
          }
        `;
      case 'secondary':
        return commonStyles.buttonSecondary;
      default:
        return commonStyles.buttonPrimary;
    }
  }}
  
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  font-size: ${designSystem.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
  white-space: nowrap;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  margin-bottom: ${designSystem.spacing.xl};
  border-bottom: 2px solid ${designSystem.colors.border.light};
  overflow-x: auto;
`;

const TabButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? designSystem.colors.primary.main : 'transparent'};
  color: ${props => props.active ? '#000000' : designSystem.colors.text.secondary};
  border: none;
  padding: ${designSystem.spacing.md} ${designSystem.spacing.lg};
  border-radius: ${designSystem.borderRadius.lg} ${designSystem.borderRadius.lg} 0 0;
  font-weight: ${designSystem.typography.fontWeight.semibold};
  font-size: ${designSystem.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  white-space: nowrap;
  position: relative;
  
  &:hover {
    background: ${props => props.active ? designSystem.colors.primary.main : designSystem.colors.primary.main + '20'};
    color: ${props => props.active ? '#000000' : designSystem.colors.primary.main};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.active ? designSystem.colors.primary.main : 'transparent'};
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.xl};
`;

const DetailCard = styled.div`
  background: ${designSystem.colors.background.tertiary};
  padding: ${designSystem.spacing.lg};
  border-radius: ${designSystem.borderRadius.lg};
  border: 1px solid ${designSystem.colors.border.light};

  h4 {
    color: ${designSystem.colors.text.primary};
    font-size: ${designSystem.typography.fontSize.lg};
    margin-bottom: ${designSystem.spacing.md};
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
  }

  .detail-item {
    margin-bottom: ${designSystem.spacing.sm};
    
    .label {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
      margin-bottom: ${designSystem.spacing.xs};
    }
    
    .value {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
    }
  }
`;

const NotesSection = styled.div`
  .notes-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${designSystem.spacing.md};
  }

  textarea {
    ${commonStyles.input}
    min-height: 120px;
    resize: vertical;
    margin-bottom: ${designSystem.spacing.md};
  }

  .notes-actions {
    display: flex;
    gap: ${designSystem.spacing.sm};
  }
`;

const BookingHistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: ${designSystem.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${designSystem.colors.border.light};
    color: ${designSystem.colors.text.primary};
  }

  th {
    background: ${designSystem.colors.background.secondary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: ${designSystem.typography.fontSize.xs};
  }

  tr:hover {
    background: ${designSystem.colors.background.secondary};
  }
`;

const RatingDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};

  .stars {
    color: ${designSystem.colors.status.warning};
    font-size: ${designSystem.typography.fontSize.lg};
  }

  .rating-value {
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.bold};
  }

  .rating-count {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
  }
`;

interface UserProfileProps {
  userId: string;
  onClose: () => void;
  onUserUpdate: () => void;
}

interface UserData {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  created_at: string;
  email_verified: boolean;
  phone_verified: boolean;
  identity_verified: boolean;
  is_blocked: boolean;
  is_vip: boolean;
  notes?: string;
  last_login?: string;
  total_bookings: number;
  total_spent: number;
  average_rating: number;
  rating_count: number;
}

const UserProfile: React.FC<UserProfileProps> = ({ userId, onClose, onUserUpdate }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [bookingHistory, setBookingHistory] = useState<any[]>([]);
  const [preferences, setPreferences] = useState<any>({});

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    try {
      // Carica dati utente base
      const { data: user } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (user) {
        // Carica statistiche bookings
        const { data: bookings } = await supabase
          .from('bookings')
          .select('*')
          .eq('user_id', userId);

        // Calcola statistiche
        const totalBookings = bookings?.length || 0;
        const totalSpent = bookings?.reduce((sum, b) => sum + (b.total_price || 0), 0) || 0;

        // Simula rating (in produzione verrebbe dal database)
        const averageRating = 4.2 + Math.random() * 0.8;
        const ratingCount = Math.floor(totalBookings * 0.7);

        setUserData({
          ...user,
          total_bookings: totalBookings,
          total_spent: totalSpent,
          average_rating: averageRating,
          rating_count: ratingCount
        });

        setNotes(user.notes || '');
        setBookingHistory(bookings || []);
      }
    } catch (error) {
      console.error('Errore caricamento utente:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (field: string, value: boolean) => {
    try {
      await supabase
        .from('user_profiles')
        .update({ [field]: value })
        .eq('id', userId);

      setUserData(prev => prev ? { ...prev, [field]: value } : null);
      onUserUpdate();
    } catch (error) {
      console.error('Errore aggiornamento stato:', error);
    }
  };

  const saveNotes = async () => {
    try {
      await supabase
        .from('user_profiles')
        .update({ notes })
        .eq('id', userId);

      setUserData(prev => prev ? { ...prev, notes } : null);
    } catch (error) {
      console.error('Errore salvataggio note:', error);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return '★'.repeat(fullStars) + 
           (hasHalfStar ? '☆' : '') + 
           '☆'.repeat(emptyStars);
  };

  if (loading) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['3xl'], marginBottom: designSystem.spacing.md }}>
            ⏳
          </div>
          Caricamento profilo utente...
        </div>
      </ProfileContainer>
    );
  }

  if (!userData) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['3xl'], marginBottom: designSystem.spacing.md }}>
            ❌
          </div>
          Utente non trovato
        </div>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <ProfileHeader>
        <Avatar>
          {getInitials(userData.full_name || userData.email)}
        </Avatar>
        
        <UserInfo>
          <h2>
            <UserIcon size={24} />
            {userData.full_name || 'Nome non specificato'}
          </h2>
          <div className="email">{userData.email}</div>
          <div className="status-badges">
            {userData.email_verified && <StatusBadge status="verified">📧 Email Verificata</StatusBadge>}
            {userData.phone_verified && <StatusBadge status="verified">📱 Telefono Verificato</StatusBadge>}
            {userData.identity_verified && <StatusBadge status="verified">🆔 Identità Verificata</StatusBadge>}
            {userData.is_vip && <StatusBadge status="vip">👑 VIP</StatusBadge>}
            {userData.is_blocked && <StatusBadge status="blocked">🚫 Bloccato</StatusBadge>}
          </div>
        </UserInfo>

        <ActionButtons>
          {!userData.identity_verified && (
            <ActionButton onClick={() => updateUserStatus('identity_verified', true)}>
              ✅ Verifica
            </ActionButton>
          )}
          
          <ActionButton 
            variant={userData.is_vip ? 'secondary' : 'primary'}
            onClick={() => updateUserStatus('is_vip', !userData.is_vip)}
          >
            {userData.is_vip ? '👑 Rimuovi VIP' : '👑 Rendi VIP'}
          </ActionButton>
          
          <ActionButton 
            variant={userData.is_blocked ? 'warning' : 'danger'}
            onClick={() => updateUserStatus('is_blocked', !userData.is_blocked)}
          >
            {userData.is_blocked ? '✅ Sblocca' : '🚫 Blocca'}
          </ActionButton>
          
          <ActionButton variant="secondary" onClick={onClose}>
            ✖️ Chiudi
          </ActionButton>
        </ActionButtons>
      </ProfileHeader>

      <TabsContainer>
        <TabButton active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
          📋 Dettagli
        </TabButton>
        <TabButton active={activeTab === 'bookings'} onClick={() => setActiveTab('bookings')}>
          🏠 Prenotazioni ({userData.total_bookings})
        </TabButton>
        <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')}>
          📝 Note Private
        </TabButton>
        <TabButton active={activeTab === 'preferences'} onClick={() => setActiveTab('preferences')}>
          ⚙️ Preferenze
        </TabButton>
      </TabsContainer>

      <TabContent>
        <AnimatePresence mode="wait">
          {activeTab === 'details' && (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <DetailGrid>
                <DetailCard>
                  <h4><UserIcon size={20} /> Informazioni Personali</h4>
                  <div className="detail-item">
                    <div className="label">Nome Completo</div>
                    <div className="value">{userData.full_name || 'Non specificato'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Email</div>
                    <div className="value">{userData.email}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Telefono</div>
                    <div className="value">{userData.phone_number || 'Non specificato'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Data Registrazione</div>
                    <div className="value">{new Date(userData.created_at).toLocaleDateString('it-IT')}</div>
                  </div>
                </DetailCard>

                <DetailCard>
                  <h4>📊 Statistiche</h4>
                  <div className="detail-item">
                    <div className="label">Prenotazioni Totali</div>
                    <div className="value">{userData.total_bookings}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Totale Speso</div>
                    <div className="value">€{userData.total_spent.toLocaleString()}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Rating Medio</div>
                    <div className="value">
                      <RatingDisplay>
                        <span className="stars">{formatStars(userData.average_rating)}</span>
                        <span className="rating-value">{userData.average_rating.toFixed(1)}</span>
                        <span className="rating-count">({userData.rating_count} reviews)</span>
                      </RatingDisplay>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Ultimo Accesso</div>
                    <div className="value">{userData.last_login ? new Date(userData.last_login).toLocaleDateString('it-IT') : 'Mai'}</div>
                  </div>
                </DetailCard>

                <DetailCard>
                  <h4>🔐 Verifiche</h4>
                  <div className="detail-item">
                    <div className="label">Email</div>
                    <div className="value">
                      {userData.email_verified ? '✅ Verificata' : '❌ Non verificata'}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Telefono</div>
                    <div className="value">
                      {userData.phone_verified ? '✅ Verificato' : '❌ Non verificato'}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Identità</div>
                    <div className="value">
                      {userData.identity_verified ? '✅ Verificata' : '❌ Non verificata'}
                    </div>
                  </div>
                </DetailCard>
              </DetailGrid>
            </motion.div>
          )}

          {activeTab === 'bookings' && (
            <motion.div
              key="bookings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <BookingHistoryTable>
                <thead>
                  <tr>
                    <th>Alloggio</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Ospiti</th>
                    <th>Totale</th>
                    <th>Status</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {bookingHistory.map(booking => (
                    <tr key={booking.id}>
                      <td>{booking.accommodation?.title_it || 'N/A'}</td>
                      <td>{new Date(booking.check_in).toLocaleDateString('it-IT')}</td>
                      <td>{new Date(booking.check_out).toLocaleDateString('it-IT')}</td>
                      <td>{booking.guests_count}</td>
                      <td>€{booking.total_price}</td>
                      <td>
                        <StatusBadge status={booking.status === 'confirmed' ? 'verified' : booking.status === 'cancelled' ? 'blocked' : 'pending'}>
                          {booking.status}
                        </StatusBadge>
                      </td>
                      <td>
                        <ActionButton variant="secondary">
                          <ExternalLinkIcon size={14} />
                          Dettagli
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </BookingHistoryTable>
              
              {bookingHistory.length === 0 && (
                <div style={{ textAlign: 'center', padding: designSystem.spacing.xl, color: designSystem.colors.text.secondary }}>
                  Nessuna prenotazione trovata
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'notes' && (
            <motion.div
              key="notes"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <NotesSection>
                <div className="notes-header">
                  <h4>📝 Note Private dell'Amministratore</h4>
                </div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Aggiungi note private su questo utente (comportamento, preferenze, problemi, ecc.)"
                />
                <div className="notes-actions">
                  <ActionButton onClick={saveNotes}>
                    <SaveIcon size={16} />
                    Salva Note
                  </ActionButton>
                </div>
              </NotesSection>
            </motion.div>
          )}

          {activeTab === 'preferences' && (
            <motion.div
              key="preferences"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <DetailGrid>
                <DetailCard>
                  <h4>🏠 Preferenze Alloggi</h4>
                  <div className="detail-item">
                    <div className="label">Tipo Preferito</div>
                    <div className="value">Appartamenti storici</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Budget Medio</div>
                    <div className="value">€200-400/notte</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Periodo Preferito</div>
                    <div className="value">Primavera/Estate</div>
                  </div>
                </DetailCard>

                <DetailCard>
                  <h4>📧 Comunicazioni</h4>
                  <div className="detail-item">
                    <div className="label">Newsletter</div>
                    <div className="value">✅ Abilitata</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Notifiche Push</div>
                    <div className="value">❌ Disabilitate</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">SMS Marketing</div>
                    <div className="value">✅ Abilitato</div>
                  </div>
                </DetailCard>

                <DetailCard>
                  <h4>🎯 Comportamento</h4>
                  <div className="detail-item">
                    <div className="label">Prenotazioni Anticipate</div>
                    <div className="value">Sì (media 45 giorni)</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Cancellazioni</div>
                    <div className="value">Basso (2%)</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Review Rate</div>
                    <div className="value">Alto (85%)</div>
                  </div>
                </DetailCard>
              </DetailGrid>
            </motion.div>
          )}
        </AnimatePresence>
      </TabContent>
    </ProfileContainer>
  );
};

export default UserProfile; 